import React, { useState, useEffect } from 'react';
import PageSkeleton from '../common/Skeleton';
import Pagination from '../common/Pagination';
import { Plus, Search, Trash2, Filter, AlertCircle, Printer, Tag, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button, IconButton, DataTable, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, EmptyState, StatusBadge, AlertMessage, PageHeader, SearchFilterBar } from '../common/ui';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateUtils';
import { printPdfBlob } from '../../utils/pdfUtils';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

const Devices = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    // Initialize from URL query params if present
    return searchParams.get('search') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    return searchParams.get('status') || getUserPreference('devices', 'statusFilter', 'all');
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => parseInt(getUserPreference('devices', 'itemsPerPage', '50')));
  // Success/error messages for main page operations
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Loading state for print all labels
  const [printingLabels, setPrintingLabels] = useState(false);
  // Selection state for bulk operations (used when statusFilter is 'draft', 'print_label' or 'active')
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  // Status counts for filter buttons
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    active: 0,
    blocked: 0,
    draft: 0,
    print_label: 0,
    lost: 0,
    retired: 0,
    demo: 0,
    sponsored: 0
  });
  
  const [sortField, setSortField] = useState(() => {
    return searchParams.get('sortField') || getUserPreference('devices', 'sortField', 'created_at');
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return searchParams.get('sortDirection') || getUserPreference('devices', 'sortDirection', 'desc');
  });
  // Live ownership data keyed by serial_number (from api.seeyou.cloud)
  const [ownershipMap, setOwnershipMap] = useState({});

  // Handle column sort
  const handleSort = (field) => {
    let newDirection = 'asc';
    
    // If clicking the same field, toggle direction
    if (field === sortField) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // If clicking a new field, default to desc for date fields (most recent first)
      newDirection = (field === 'created_at' || field === 'updated_at') ? 'desc' : 'asc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    setUserPreference('devices', 'sortField', field);
    setUserPreference('devices', 'sortDirection', newDirection);
    
    // Update URL params while preserving search and status filter (reset to page 1)
    updateUrlParams(searchTerm, statusFilter, field, newDirection);
    
    // Fetch data with new sort params (reset to page 1)
    setCurrentPage(1);
    fetchDevices(1, itemsPerPage, searchTerm, statusFilter, field, newDirection);
  };

  // Fetch devices on component mount
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    const statusFromUrl = searchParams.get('status') || getUserPreference('devices', 'statusFilter', 'all');
    const sortFieldFromUrl = searchParams.get('sortField') || getUserPreference('devices', 'sortField', 'created_at');
    const sortDirectionFromUrl = searchParams.get('sortDirection') || getUserPreference('devices', 'sortDirection', 'desc');
    
    fetchDevices(1, itemsPerPage, searchFromUrl, statusFromUrl, sortFieldFromUrl, sortDirectionFromUrl);
    fetchStatusCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to URL parameter changes (for browser back/forward)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    const statusFromUrl = searchParams.get('status') || getUserPreference('devices', 'statusFilter', 'all');
    const sortFieldFromUrl = searchParams.get('sortField') || getUserPreference('devices', 'sortField', 'created_at');
    const sortDirectionFromUrl = searchParams.get('sortDirection') || getUserPreference('devices', 'sortDirection', 'desc');
    
    // Only update if values actually changed
    if (searchFromUrl !== searchTerm || statusFromUrl !== statusFilter ||
        sortFieldFromUrl !== sortField || sortDirectionFromUrl !== sortDirection) {
      setSearchTerm(searchFromUrl);
      setStatusFilter(statusFromUrl);
      setSortField(sortFieldFromUrl);
      setSortDirection(sortDirectionFromUrl);
      setCurrentPage(1);
      fetchDevices(1, itemsPerPage, searchFromUrl, statusFromUrl, sortFieldFromUrl, sortDirectionFromUrl);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatusCounts = async () => {
    try {
      const response = await axios.get('/api/devices/stats/simple');
      const stats = response.data;
      setStatusCounts({
        all: stats.total || 0,
        active: stats.active || 0,
        blocked: stats.blocked || 0,
        draft: stats.draft || 0,
        print_label: stats.print_label || 0,
        lost: stats.lost || 0,
        retired: stats.retired || 0,
        demo: stats.demo || 0,
        sponsored: stats.sponsored || 0
      });
    } catch (error) {
      console.error('Error fetching status counts:', error);
    }
  };

  const fetchDevices = async (page = 1, limit = itemsPerPage, searchOverride, statusOverride, sort = sortField, direction = sortDirection) => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      const effectiveSearch = searchOverride !== undefined ? searchOverride : searchTerm;
      if (effectiveSearch) params.set('search', effectiveSearch);
      const effectiveStatus = statusOverride !== undefined ? statusOverride : statusFilter;
      if (effectiveStatus && effectiveStatus !== 'all' && !effectiveSearch) params.set('status', effectiveStatus);
      // Send sort params to server for proper pagination
      if (sort) params.set('sortField', sort);
      if (direction) params.set('sortDirection', direction);
      
      const response = await axios.get(`/api/devices?${params.toString()}`);
      console.log('Devices API response:', response.data); // Debug log
      
      // Handle paginated devices response  
      const devicesData = response.data.devices || response.data;
      
      // Ensure we have an array
      if (Array.isArray(devicesData)) {
        setDevices(devicesData);

        // Fetch live ownership data for this page via the backend proxy (/api/ownership)
        const serials = devicesData.map(d => d.serial_number).filter(Boolean);
        if (serials.length > 0) {
          axios.get(`/api/ownership?serials=${serials.map(encodeURIComponent).join(',')}`)
            .then(ownershipRes => {
              setOwnershipMap(prev => ({ ...prev, ...ownershipRes.data.results }));
            })
            .catch(err => console.warn('[Devices] ownership fetch error:', err.message));
        }

        // Handle pagination metadata
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
          setTotalItems(response.data.pagination.totalItems || devicesData.length);
          setCurrentPage(response.data.pagination.currentPage || page);
        } else {
          // Fallback for backends that don't return pagination metadata
          setTotalPages(devicesData.length === limit ? page + 1 : page);
          setTotalItems(devicesData.length);
          setCurrentPage(page);
        }
      } else {
        console.error('Devices data is not an array:', devicesData);
        setDevices([]); // Set empty array as fallback
        setError('Invalid data format received from server');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices. Please try again.');
      setDevices([]); // Set empty array on error
      setLoading(false);
    }
  };

  // handleSubmit removed - now using unified DeviceEdit form

  const handleView = (device) => {
    navigate(`/devices/id/${device.id}/view`);
  };

  const handleDelete = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        setError('');
        setSuccess('');
        await axios.delete(`/api/devices/${deviceId}`);
        setSuccess('Device deleted successfully!');
        fetchDevices();
        fetchStatusCounts(); // Update status counts after deletion
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting device:', error);
        setError('Failed to delete device. Please try again.');
      }
    }
  };

  // Form handling functions removed - now using unified DeviceEdit form

  const runSearch = async () => {
    setCurrentPage(1);
    // Update URL params
    updateUrlParams(searchTerm, statusFilter);
    fetchDevices(1);
  };

  const runSearchWithValue = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    // Update URL params
    updateUrlParams(value, statusFilter);
    // Fetch immediately using the selected value to avoid setState race
    fetchDevices(1, itemsPerPage, value);
  };

  // Helper function to update URL parameters
  const updateUrlParams = (search, status, sort = sortField, direction = sortDirection) => {
    const newParams = new URLSearchParams();
    
    // Only add non-default values to keep URLs clean
    if (search && search.trim()) {
      newParams.set('search', search.trim());
    }
    if (status && status !== 'all') {
      newParams.set('status', status);
    }
    if (sort && sort !== 'created_at') {
      newParams.set('sortField', sort);
    }
    if (direction && direction !== 'desc') {
      newParams.set('sortDirection', direction);
    }
    
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchDevices(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setUserPreference('devices', 'itemsPerPage', newItemsPerPage);
    setCurrentPage(1);
    fetchDevices(1, newItemsPerPage);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setUserPreference('devices', 'statusFilter', newStatus);
    setCurrentPage(1);
    // Update URL params
    updateUrlParams(searchTerm, newStatus);
    // Pass the new status directly to fetchDevices to avoid race condition
    fetchDevices(1, itemsPerPage, undefined, newStatus);
  };

  const handleStatusClick = (status) => {
    handleStatusFilterChange(status);
  };

  // Clear selections when filter or search changes
  useEffect(() => {
    setSelectedDeviceIds(new Set());
    setLastSelectedIndex(null);
  }, [statusFilter, searchTerm]);

  // Status filters that support bulk selection, mapped to their available bulk actions
  const bulkActionsByFilter = {
    draft: [{ targetStatus: 'print_label', buttonLabel: 'Print Label', displayName: 'Print label' }],
    print_label: [{ targetStatus: 'draft', buttonLabel: 'Move to Draft', displayName: 'Draft' }],
    active: [
      { targetStatus: 'draft', buttonLabel: 'Move to Draft', displayName: 'Draft' },
      { targetStatus: 'print_label', buttonLabel: 'Move to Print Label', displayName: 'Print label' }
    ]
  };
  const bulkSelectionEnabled = Object.prototype.hasOwnProperty.call(bulkActionsByFilter, statusFilter);

  // Selection handlers (only used when bulk selection is enabled for the current filter)
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(devices.map(d => d.id));
      setSelectedDeviceIds(allIds);
    } else {
      setSelectedDeviceIds(new Set());
    }
    setLastSelectedIndex(null);
  };

  const handleDeviceSelect = (deviceId, index, e) => {
    if ('stopPropagation' in e) {
      e.stopPropagation();
    }
    const newSelection = new Set(selectedDeviceIds);

    if (e.shiftKey && lastSelectedIndex !== null) {
      // Range selection with Shift+Click
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeDevices = devices.slice(start, end + 1);
      rangeDevices.forEach(d => newSelection.add(d.id));
    } else if (e.ctrlKey || e.metaKey) {
      // Toggle individual selection with Ctrl/Cmd+Click
      if (newSelection.has(deviceId)) {
        newSelection.delete(deviceId);
      } else {
        newSelection.add(deviceId);
      }
    } else {
      // Simple click - toggle single item
      if (newSelection.has(deviceId)) {
        newSelection.delete(deviceId);
      } else {
        newSelection.add(deviceId);
      }
    }

    setSelectedDeviceIds(newSelection);
    setLastSelectedIndex(index);
  };

  // Bulk operation: Change status of selected devices
  const handleBulkStatusChange = async (targetStatus, displayName) => {
    if (selectedDeviceIds.size === 0) return;

    setBulkOperationLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/devices/batch-status', {
        device_ids: Array.from(selectedDeviceIds),
        status: targetStatus
      });

      if (response.data.success) {
        const { updated_count, skipped_count } = response.data;
        let message = `Successfully changed ${updated_count} device${updated_count !== 1 ? 's' : ''} to "${displayName}" status.`;
        if (skipped_count > 0) {
          message += ` (${skipped_count} already had that status)`;
        }
        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);

        // Clear selections
        setSelectedDeviceIds(new Set());
        setLastSelectedIndex(null);

        // Refresh device list and status counts
        fetchStatusCounts();
        fetchDevices(currentPage, itemsPerPage);
      }
    } catch (err) {
      console.error('Error in bulk status update:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update device statuses';
      setError(errorMessage);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  // Calculate select all state (only for filters with bulk selection)
  const allSelected = bulkSelectionEnabled && devices.length > 0 && devices.every(d => selectedDeviceIds.has(d.id));
  const someSelected = bulkSelectionEnabled && devices.some(d => selectedDeviceIds.has(d.id));
  const isIndeterminate = someSelected && !allSelected;

  const handlePrintAllLabels = async () => {
    try {
      setPrintingLabels(true);
      setError('');
      setSuccess('');
      
      const response = await axios.post('/api/devices/print-labels', {}, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      await printPdfBlob(blob);
      
      // Get the count of devices that were updated to Active
      const devicesUpdated = response.headers['x-devices-updated'] || statusCounts.print_label;
      
      // Refresh status counts and device list since statuses changed
      fetchStatusCounts();
      fetchDevices(currentPage, itemsPerPage);
      
      setSuccess(`Labels PDF generated successfully for ${devicesUpdated} device(s). Status changed to Active.`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error generating labels PDF:', error);
      if (error.response?.status === 404) {
        setError('No devices with "Print label" status found');
      } else {
        setError('Failed to generate labels PDF. Please try again.');
      }
    } finally {
      setPrintingLabels(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Devices</h1>
        <PageSkeleton rows={3} rowHeight="h-20" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-0 pb-20">
      <PageHeader
        title="Devices"
        subtitle="Manage your device inventory"
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/catalog/products')}
            >
              Products
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/catalog/modules')}
            >
              Modules
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handlePrintAllLabels}
              disabled={statusCounts.print_label === 0 || printingLabels}
              icon={Printer}
            >
              {printingLabels ? 'Generating...' : `Print all labels (${statusCounts.print_label})`}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/devices/select-product')}
              icon={Plus}
            >
              Add Device
            </Button>
          </>
        }
      />

      <SearchFilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search devices..."
        useDeviceAutocomplete={true}
        includeAllStatuses={true}
        onDeviceSelect={(device) => {
          if (!device) return null;
          // Use serial_number if available, otherwise use product_name or name
          const searchValue = device.serial_number || device.product_name || device.name;
          return runSearchWithValue(searchValue);
        }}
        onSearch={runSearch}
        onSearchSubmit={runSearch}
        onSearchClear={() => runSearchWithValue('')}
      />

      {/* Main Error Display */}
      {error && <AlertMessage type="error" message={error} />}

      {/* Main Success Display */}
      {success && <AlertMessage type="success" message={success} />}

      {/* Status Filter Buttons */}
      <div className="mb-3 flex flex-wrap gap-3">
        <button
          onClick={() => handleStatusClick('all')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'all'
              ? 'bg-naviter-blue text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'all' ? 'bg-naviter-blue-dark' : 'bg-gray-200'
          }`}>
            {statusCounts.all}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('draft')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'draft'
              ? 'bg-slate-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Draft
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'draft' ? 'bg-slate-500' : 'bg-gray-200'
          }`}>
            {statusCounts.draft}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('print_label')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'print_label'
              ? 'bg-cyan-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Print label
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'print_label' ? 'bg-cyan-500' : 'bg-gray-200'
          }`}>
            {statusCounts.print_label}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('active')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'active'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Active
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'active' ? 'bg-green-500' : 'bg-gray-200'
          }`}>
            {statusCounts.active}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('demo')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'demo'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Demo
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'demo' ? 'bg-blue-500' : 'bg-gray-200'
          }`}>
            {statusCounts.demo}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('sponsored')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'sponsored'
              ? 'bg-purple-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Sponsored
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'sponsored' ? 'bg-purple-500' : 'bg-gray-200'
          }`}>
            {statusCounts.sponsored}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('blocked')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'blocked'
              ? 'bg-red-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Blocked
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'blocked' ? 'bg-red-500' : 'bg-gray-200'
          }`}>
            {statusCounts.blocked}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('lost')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'lost'
              ? 'bg-yellow-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Lost
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'lost' ? 'bg-yellow-500' : 'bg-gray-200'
          }`}>
            {statusCounts.lost}
          </span>
        </button>
        
        <button
          onClick={() => handleStatusClick('retired')}
          className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
            statusFilter === 'retired'
              ? 'bg-gray-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Retired
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            statusFilter === 'retired' ? 'bg-gray-500' : 'bg-gray-200'
          }`}>
            {statusCounts.retired}
          </span>
        </button>
      </div>

      {/* Bulk Actions Bar (only for filters with bulk selection) */}
      {bulkSelectionEnabled && !loading && selectedDeviceIds.size > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-900">
              {selectedDeviceIds.size} device{selectedDeviceIds.size > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            {bulkActionsByFilter[statusFilter].map((action) => (
              <Button
                key={action.targetStatus}
                variant="primary"
                onClick={() => handleBulkStatusChange(action.targetStatus, action.displayName)}
                disabled={bulkOperationLoading || selectedDeviceIds.size === 0}
                icon={Tag}
              >
                {bulkOperationLoading ? 'Updating...' : action.buttonLabel}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Top Pagination */}
      <Pagination
        position="top"
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Devices Table */}
      <DataTable
        emptyState={devices.length === 0 ? (
          <EmptyState 
            message="No devices found"
            actionText="Add your first device"
            onActionClick={() => navigate('/devices/new')}
          />
        ) : null}
      >
        <TableHeader>
          <tr>
            {bulkSelectionEnabled && (
              <TableHeaderCell align="center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out cursor-pointer"
                  title="Select all devices"
                />
              </TableHeaderCell>
            )}
            <TableHeaderCell className="w-px whitespace-nowrap">Device</TableHeaderCell>
            <TableHeaderCell className="w-px whitespace-nowrap">Product</TableHeaderCell>
            <TableHeaderCell responsive="lg" className="text-center">Status</TableHeaderCell>
            <TableHeaderCell responsive="xl" className="w-[320px] min-w-[320px] max-w-[320px]">Owner</TableHeaderCell>
            <TableHeaderCell responsive="lg">Order</TableHeaderCell>
            <TableHeaderCell 
              responsive="md"
              className="cursor-pointer select-none hover:bg-gray-50"
              onClick={() => handleSort('created_at')}
            >
              <div className="flex items-center gap-2">
                <span>Created</span>
                {sortField === 'created_at' ? (
                  sortDirection === 'asc' ? (
                    <ArrowUp className="w-4 h-4 text-naviter-blue" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-naviter-blue" />
                  )
                ) : (
                  <ArrowUpDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </TableHeaderCell>
            <TableHeaderCell align="center">Actions</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {(devices || []).map((device, index) => {
            const isSelected = selectedDeviceIds.has(device.id);
            
            return (
              <TableRow
                key={device.id}
                clickable={true}
                href={`/devices/id/${device.id}/view`}
                onClick={() => handleView(device)}
              >
                {bulkSelectionEnabled && (
                  <TableCell align="center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeviceSelect(device.id, index, e);
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleDeviceSelect(device.id, index, e);
                      }}
                      className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out cursor-pointer"
                      title="Select device"
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className="text-sm font-medium text-gray-900">{device.serial_number}</div>
                </TableCell>
                <TableCell>{device.product_name || device.model || 'N/A'}</TableCell>
                <TableCell responsive="lg">
                  <div className="flex flex-col items-center">
                    <StatusBadge status={device.status} variant="device" />
                    {device.status === 'donated' && device.donated_to_repair_id && (
                      <a
                        href={`/repairs/${device.donated_to_repair_id}/view`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          navigate(`/repairs/${device.donated_to_repair_id}/view`);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 text-center"
                      >
                        Repair #{device.donated_to_repair_id}
                      </a>
                    )}
                    {device.last_activity_updater && device.last_activity_updater !== 'Unknown' && (
                      <div className="text-sm text-gray-500 mt-1">
                        {device.last_activity_updater}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell responsive="xl" className="max-w-xs">
                  {(() => {
                    const liveOwner = ownershipMap[device.serial_number]?.owner;
                    const ownerName = liveOwner
                      ? ([liveOwner.first_name, liveOwner.last_name].filter(Boolean).join(' ') || liveOwner.email)
                      : device.owner_name;
                    const ownerTitle = liveOwner ? liveOwner.email : 'Legacy database value';
                    const buyerName = device.order_company_name?.trim() || device.order_customer_name;

                    if (!ownerName && !buyerName) {
                      return <div className="truncate">-</div>;
                    }

                    return (
                      <>
                        {ownerName && (
                          <div className="truncate" title={ownerTitle}>
                            {ownerName}
                          </div>
                        )}
                        {buyerName && (
                          <div
                            className="truncate text-gray-500 italic"
                            title={ownerName ? 'Buyer (from order)' : 'From order (no owner registered)'}
                          >
                            {buyerName}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </TableCell>
                <TableCell responsive="lg">
                  {device.order_id && device.order_number ? (
                    <a
                      href={`https://b2b.naviter.com/wp-admin/post.php?post=${device.order_number}&action=edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      #{device.order_number}
                    </a>
                  ) : '-'}
                </TableCell>
                <TableCell responsive="md" className="whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatDateTime(device.created_at)}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Updated: {formatDateTime(device.updated_at || device.created_at)}
                  </div>
                </TableCell>
                <TableCell align="center" className="text-sm font-medium">
                  <div className="flex justify-center space-x-2">
                    <IconButton
                      variant="delete"
                      icon={Trash2}
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(device.id); }}
                      title="Delete"
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </DataTable>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Modal removed - now using unified DeviceEdit page */}
    </div>
  );
};

export default Devices;
